import {
  bindExternalStoreMessage,
  getExternalStoreMessages,
  ExportedMessageRepository,
  type Attachment,
  type AttachmentAdapter,
  type CompleteAttachment,
  type PendingAttachment,
  type EditComposerRuntime,
  type ThreadRuntime,
} from '@assistant-ui/react';
import type { UIMessage } from 'ai';
import {
  loadServerThreadBranch,
  loadServerThreadMessages,
  seedServerThread,
} from '../server-threads';
import { randomUuid } from './random-uuid';
import { apiUrl, authHeaders } from './shell';
import { preserveRepairDuringOriginal } from './repair-branch-fence';

type FilePart = Extract<UIMessage['parts'][number], { type: 'file' }>;
type StoredRepair = {
  id: string;
  parentId: string | null;
  parts: UIMessage['parts'];
  headId: string;
};
type RepairOptions = {
  threadId: string;
  thread: ThreadRuntime;
  attachments: AttachmentAdapter;
  isCurrentOwner: () => boolean;
  selected: (messageId: string) => void;
};
function current(options: RepairOptions) {
  if (!options.isCurrentOwner())
    throw new Error('Your workspace changed. Reload the chat before editing.');
}
async function digest(value: string) {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
function attachmentPart(attachment: CompleteAttachment): FilePart {
  const part = attachment.content[0];
  if (part?.type === 'file')
    return { type: 'file', filename: attachment.name, mediaType: part.mimeType, url: part.data };
  if (part?.type === 'image')
    return {
      type: 'file',
      filename: attachment.name,
      mediaType: attachment.contentType ?? 'image/png',
      url: part.image,
    };
  throw new Error('This attachment cannot be saved. Remove it and choose the file again.');
}
function storedReference(part: FilePart) {
  if (part.url.startsWith('/api/v1/threads/')) return true;
  if (!part.url.startsWith('data:text/plain;charset=utf-8,') || part.url.length > 8192)
    return false;
  try {
    return decodeURIComponent(part.url.slice(part.url.indexOf(',') + 1)).startsWith(
      'Uploaded file "',
    );
  } catch {
    return false;
  }
}
async function originalFile(part: FilePart) {
  if (!part.url.startsWith('data:'))
    throw new Error('This old attachment is unavailable. Remove it or choose the file again.');
  // Local retained data only. Never acquire an arbitrary URL during repair.
  const response = await fetch(part.url);
  const blob = await response.blob();
  return new File([blob], part.filename ?? 'attachment.txt', { type: blob.type });
}
async function uploaded(options: RepairOptions, file: File): Promise<CompleteAttachment> {
  const added = options.attachments.add({ file });
  let pending;
  if (Symbol.asyncIterator in added) for await (const state of added) pending = state;
  else pending = await added;
  if (!pending) throw new Error('The file could not be uploaded. Remove it or choose it again.');
  current(options);
  return options.attachments.send(pending);
}
function isPendingAttachment(attachment: Attachment): attachment is PendingAttachment {
  return (
    attachment.status.type !== 'complete' && 'file' in attachment && attachment.file instanceof File
  );
}
async function complete(
  options: RepairOptions,
  attachment: Attachment,
): Promise<CompleteAttachment> {
  if (attachment.status.type === 'complete') return attachment as CompleteAttachment;
  if (!isPendingAttachment(attachment))
    throw new Error('The file is unavailable. Remove it or choose it again.');
  return options.attachments.send(attachment);
}

export function createMessageRepair(options: RepairOptions) {
  const attempts = new Map<string, string>();
  return async (composer: EditComposerRuntime): Promise<void> => {
    current(options);
    const running = options.thread.getState();
    const originalParent = running.isRunning
      ? [...running.messages].reverse().find((message) => message.role === 'user')?.id
      : undefined;
    const originalAnswer = running.isRunning
      ? [...running.messages]
          .reverse()
          .find(
            (message) =>
              message.role === 'assistant' &&
              getExternalStoreMessages<UIMessage>(message).some(
                (wire) => wire.role === 'assistant',
              ),
          )?.id
      : undefined;
    const state = composer.getState();
    if (!state.sourceId) throw new Error('This message is unavailable. Reload the chat.');
    const sourceId = state.sourceId;
    // Read the current durable head and source independently of the rejected
    // model body. Saving this edit never needs a model or payment admission.
    await seedServerThread(options.threadId, options.isCurrentOwner);
    current(options);
    const source = loadServerThreadMessages(options.threadId).find(
      (message) => message.id === sourceId,
    );
    const kept: number[] = [];
    const hashes: { index: number; sha256: string }[] = [];
    const parts: FilePart[] = [];
    const attachments: CompleteAttachment[] = [];
    for (const attachment of state.attachments) {
      const saved = await complete(options, attachment);
      let part = attachmentPart(saved);
      if (!storedReference(part)) {
        const replacement = await uploaded(options, await originalFile(part));
        attachments.push(replacement);
        part = attachmentPart(replacement);
        parts.push(part);
        continue;
      }
      attachments.push(saved);
      const index =
        source?.parts.findIndex(
          (candidate, i) =>
            candidate.type === 'file' && candidate.url === part.url && !kept.includes(i),
        ) ?? -1;
      if (index >= 0) {
        kept.push(index);
        hashes.push({ index, sha256: await digest(part.url) });
      } else parts.push(part);
    }
    current(options);
    const head =
      loadServerThreadBranch(options.threadId)?.headId ??
      loadServerThreadMessages(options.threadId).at(-1)?.id ??
      null;
    const attempt = JSON.stringify({ sourceId, text: state.text, hashes, parts });
    const id = attempts.get(attempt) ?? randomUuid();
    attempts.set(attempt, id);
    let response: Response;
    if (source) {
      response = await fetch(apiUrl(`/api/v1/threads/${options.threadId}/repair`), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id,
          sourceId,
          expectedHeadId: head,
          text: state.text,
          retainedPartIndexes: kept,
          retainedPartHashes: hashes,
          parts,
        }),
      });
    } else {
      // A body-limit failure can precede the first durable mirror. Use the
      // ordinary bounded owner append with the actual current parent; never
      // fabricate an absent original or ask to resend its base64 conversation.
      response = await fetch(apiUrl(`/api/v1/threads/${options.threadId}/messages`), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id,
          role: 'user',
          parts: [{ type: 'text', text: state.text }, ...parts],
          parentId: head,
          expectedHeadId: head,
        }),
      });
    }
    const body = await response.json().catch(() => null);
    if (!response.ok)
      throw new Error(body?.message ?? 'This chat changed. Reload it before saving your edit.');
    current(options);
    const saved: StoredRepair = source
      ? body.message
      : { id, parentId: head, headId: id, parts: [{ type: 'text', text: state.text }, ...parts] };
    if (saved.headId !== saved.id || !Array.isArray(saved.parts))
      throw new Error('This chat changed. Reload it before saving your edit.');
    const existing = options.thread.export();
    const projected = ExportedMessageRepository.fromBranchableArray([
      {
        parentId: saved.parentId,
        message: {
          id: saved.id,
          role: 'user',
          content: [{ type: 'text', text: state.text }],
          attachments,
          metadata: { custom: {} },
        },
      },
    ]).messages[0]!;
    bindExternalStoreMessage(projected.message, {
      id: saved.id,
      role: 'user',
      parts: saved.parts,
    } satisfies UIMessage);
    if (originalParent)
      preserveRepairDuringOriginal(
        options.thread,
        originalParent,
        originalAnswer,
        saved.id,
        options.isCurrentOwner,
      );
    options.thread.import({ headId: saved.id, messages: [...existing.messages, projected] });
    composer.cancel();
    options.selected(saved.id);
    await seedServerThread(options.threadId, options.isCurrentOwner);
  };
}
